import { t as uiText } from '../i18n';
import { useState, useCallback, useEffect } from 'react';
import {
  collection,
  addDoc,
  updateDoc,
  doc,
  getDocs,
  deleteDoc,
  orderBy,
  query,
  limit,
  serverTimestamp,
} from 'firebase/firestore';
import { db } from '../lib/firebase';
import type { ChatMessage } from './useAgentChat';
import type { IngestFlowState } from '../utils/agentIngest';
import { normalizeIngestFlow } from '../utils/agentIngest';

export interface AgentChatSession {
  id: string;
  title: string;
  createdAt: Date;
  updatedAt: Date;
  messages: ChatMessage[];
  ingestFlow?: IngestFlowState | null;
}

export interface UseAgentChatHistoryResult {
  sessions: AgentChatSession[];
  loading: boolean;
  saveSession: (
    messages: ChatMessage[],
    workspaceId: string,
    ingestFlow?: IngestFlowState
  ) => Promise<string | null>;
  deleteSession: (sessionId: string, workspaceId: string) => Promise<void>;
  /** Delete drafts that were never started (no user message). */
  discardEmptySessions: (workspaceId: string, opts?: { keepId?: string }) => Promise<void>;
  refresh: (workspaceId: string) => Promise<void>;
}

function deriveTitle(messages: ChatMessage[]): string {
  const first = messages.find((m) => m.role === 'user');
  if (!first) return uiText("(New conversation)");
  return first.content.slice(0, 40) + (first.content.length > 40 ? '…' : '');
}

export function useAgentChatHistory(): UseAgentChatHistoryResult {
  const [sessions, setSessions] = useState<AgentChatSession[]>([]);
  const [loading, setLoading] = useState(false);

  const refresh = useCallback(async (workspaceId: string) => {
    setLoading(true);
    try {
      const snap = await getDocs(
        query(
          collection(db, `workspaces/${workspaceId}/agentChatSessions`),
          orderBy('updatedAt', 'desc'),
          limit(30)
        )
      );
      const list: AgentChatSession[] = snap.docs.map((d) => {
        const data = d.data() as any;
        return {
          id: d.id,
          title: data.title ?? uiText("(Untitled)"),
          createdAt: data.createdAt?.toDate?.() ?? new Date(),
          updatedAt: data.updatedAt?.toDate?.() ?? new Date(),
          messages: (data.messages ?? []) as ChatMessage[],
          ingestFlow: normalizeIngestFlow(data.ingestFlow),
        };
      });
      setSessions(list);
    } catch (err) {
      console.error('[useAgentChatHistory] refresh error', err);
    } finally {
      setLoading(false);
    }
  }, []);

  const saveSession = useCallback(
    async (
      messages: ChatMessage[],
      workspaceId: string,
      ingestFlow?: IngestFlowState
    ): Promise<string | null> => {
      if (messages.length === 0) return null;
      const title = deriveTitle(messages);
      // Strip non-serializable fields for Firestore
      const serializable = messages.map((m) => ({
        id: m.id,
        role: m.role,
        content: m.content,
        ...(m.executedAction ? { executedAction: { label: m.executedAction.label, success: m.executedAction.success, resultText: m.executedAction.resultText ?? '' } } : {}),
        ...(m.choices ? { choices: m.choices } : {}),
        ...(m.crawlTargetList ? { crawlTargetList: m.crawlTargetList } : {}),
        ...(m.pagePreviewList ? { pagePreviewList: m.pagePreviewList } : {}),
        ...(m.createdPage ? { createdPage: m.createdPage } : {}),
      }));
      try {
        const ref = await addDoc(
          collection(db, `workspaces/${workspaceId}/agentChatSessions`),
          {
            title,
            messages: serializable,
            ...(ingestFlow ? { ingestFlow } : {}),
            workspaceId,
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
          }
        );
        return ref.id;
      } catch (err) {
        console.error('[useAgentChatHistory] saveSession error', err);
        return null;
      }
    },
    []
  );

  const discardEmptySessions = useCallback(async (workspaceId: string, opts?: { keepId?: string }) => {
    if (!workspaceId) return;
    try {
      const snap = await getDocs(
        query(
          collection(db, `workspaces/${workspaceId}/agentChatSessions`),
          orderBy('updatedAt', 'desc'),
          limit(50)
        )
      );
      const toDelete = snap.docs.filter((d) => {
        if (opts?.keepId && d.id === opts.keepId) return false;
        const messages = (d.data().messages ?? []) as ChatMessage[];
        return !messages.some((m) => m.role === 'user');
      });
      await Promise.all(toDelete.map((d) => deleteDoc(d.ref)));
      if (toDelete.length > 0) {
        const deleted = new Set(toDelete.map((d) => d.id));
        setSessions((prev) => prev.filter((s) => !deleted.has(s.id)));
      }
    } catch (err) {
      console.error('[useAgentChatHistory] discardEmptySessions error', err);
    }
  }, []);

  const deleteSession = useCallback(async (sessionId: string, workspaceId: string) => {
    try {
      await deleteDoc(doc(db, `workspaces/${workspaceId}/agentChatSessions`, sessionId));
      setSessions((prev) => prev.filter((s) => s.id !== sessionId));
    } catch (err) {
      console.error('[useAgentChatHistory] deleteSession error', err);
    }
  }, []);

  return { sessions, loading, saveSession, deleteSession, discardEmptySessions, refresh };
}
